import { useEffect, useState } from "react";
import { api, formatApiError, money } from "@/lib/api";
import { Plus, Users, X, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const POSITIONS = [
    ["paramedico", "Paramédico"],
    ["operador", "Operador / Chofer"],
    ["medico", "Médico"],
    ["enfermera", "Enfermería"],
    ["supervisor", "Supervisor"],
    ["cabina", "Cabina / Despacho"],
    ["coordinador", "Coordinador"],
    ["administrativo", "Administrativo"],
];

const EMPLOYMENT_TYPES = [
    { value: "planta", label: "Planta / Nómina", hint: "Sueldo mensual fijo" },
    { value: "eventual", label: "Eventual / Por evento", hint: "Cobra por cada servicio o evento cubierto" },
    { value: "por_hora", label: "Por hora", hint: "Tarifa fija por hora trabajada (ej. paramédico pie tierra)" },
    { value: "por_guardia", label: "Por guardia", hint: "Tarifa fija por turno 12h o 24h" },
    { value: "freelance", label: "Freelance / Honorarios", hint: "Factura aparte, cobra por servicio" },
];

const PAY_PERIODS = [
    ["mensual", "Mensual"],
    ["quincenal", "Quincenal"],
    ["semanal", "Semanal"],
    ["por_servicio", "Cada servicio / evento"],
];

const EMPTY = {
    name: "", position: "paramedico", phone: "", email: "", base: "", salary: 0, status: "activo",
    rfc: "", curp: "", license_number: "", license_expires: "", emergency_contact: "", emergency_phone: "",
    hire_date: "", notes: "",
    employment_type: "planta",
    hourly_rate: 0, shift_rate_12h: 0, shift_rate_24h: 0,
    per_event_rate: 0, bonus_per_service: 0,
    pay_period: "mensual",
    payroll_notes: "",
};

export default function Personnel() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        try {
            const r = await api.get("/personnel");
            if (r?.data) setList(r.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (p) => {
        setEditingId(p.id);
        setForm({ ...EMPTY, ...p });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/personnel/${editingId}`, form);
                toast.success("Personal actualizado");
            } else {
                await api.post("/personnel", form);
                toast.success("Personal creado");
            }
            setOpen(false); setEditingId(null); setForm(EMPTY);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`¿Eliminar a "${p.name}"?\n\nEsta acción se registra en Auditoría. Si esta persona tiene servicios asignados, mejor cambia su estatus a "baja" en vez de eliminar.`)) return;
        try {
            await api.delete(`/personnel/${p.id}`);
            toast.success("Personal eliminado");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleStatus = async (p) => {
        const next = p.status === "activo" ? "baja" : "activo";
        try {
            await api.put(`/personnel/${p.id}`, { ...p, status: next });
            toast.success(`Estatus cambiado a ${next}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="personnel-page">
            <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">{list.length} colaborador(es) · {list.filter(p => p.status === "activo").length} activos</div>
                <button onClick={openNew} className="fm-btn-accent" data-testid="new-personnel-btn"><Plus className="w-3.5 h-3.5" /> Nuevo colaborador</button>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Nombre", "Puesto", "Esquema", "Compensación", "Base", "Estatus", "Acciones"].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {list.map((p) => {
                            const empType = p.employment_type || "planta";
                            const empLabel = EMPLOYMENT_TYPES.find((t) => t.value === empType)?.label || "Planta";
                            // Resumen de compensación según esquema
                            let compSummary = "—";
                            if (empType === "planta") compSummary = `${money(p.salary)} / mes`;
                            else if (empType === "por_hora") compSummary = `${money(p.hourly_rate)} / h`;
                            else if (empType === "por_guardia") {
                                const parts = [];
                                if (p.shift_rate_12h) parts.push(`12h ${money(p.shift_rate_12h)}`);
                                if (p.shift_rate_24h) parts.push(`24h ${money(p.shift_rate_24h)}`);
                                compSummary = parts.join(" · ") || "—";
                            } else if (empType === "eventual" || empType === "freelance") {
                                const parts = [];
                                if (p.per_event_rate) parts.push(`${money(p.per_event_rate)} / evento`);
                                if (p.bonus_per_service) parts.push(`+${money(p.bonus_per_service)} bono`);
                                compSummary = parts.join(" · ") || "—";
                            }
                            return (
                            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-medium">
                                    <div>{p.name}</div>
                                    {p.phone && <div className="text-[10px] text-slate-500 font-mono">{p.phone}</div>}
                                </td>
                                <td className="py-2 px-3"><span className="fm-badge fm-badge-neutral capitalize">{p.position}</span></td>
                                <td className="py-2 px-3"><span className={`fm-badge ${empType === "planta" ? "fm-badge-positive" : "fm-badge-warning"}`}>{empLabel}</span></td>
                                <td className="py-2 px-3 text-right font-mono text-xs">{compSummary}</td>
                                <td className="py-2 px-3 text-xs">{p.base || "—"}</td>
                                <td className="py-2 px-3">
                                    <button
                                        onClick={() => toggleStatus(p)}
                                        className={`fm-badge cursor-pointer hover:opacity-80 ${p.status === "activo" ? "fm-badge-positive" : "fm-badge-neutral"}`}
                                        title="Click para activar/dar de baja"
                                        data-testid={`toggle-status-${p.id}`}
                                    >
                                        {p.status}
                                    </button>
                                </td>
                                <td className="py-2 px-3">
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => openEdit(p)} className="text-slate-600 hover:text-red-600 inline-flex items-center gap-1 text-xs" title="Editar" data-testid={`edit-personnel-${p.id}`}>
                                            <Edit3 className="w-3.5 h-3.5" /> Editar
                                        </button>
                                        <button onClick={() => remove(p)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs" title="Eliminar" data-testid={`delete-personnel-${p.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
                {list.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500"><Users className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin personal registrado.</div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">{editingId ? "Editar colaborador" : "Nuevo colaborador"}</h2>
                            <button onClick={() => { setOpen(false); setEditingId(null); }}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2"><label className="fm-label">Nombre *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fm-input" data-testid="p-name" autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false} /></div>
                                <div><label className="fm-label">Puesto</label><select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="fm-input">{POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                                <div><label className="fm-label">Estatus</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fm-input"><option value="activo">Activo</option><option value="baja">Baja</option><option value="suspendido">Suspendido</option><option value="vacaciones">Vacaciones</option></select></div>
                                <div><label className="fm-label">Base</label><input value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })} className="fm-input" placeholder="Base norte / Sur / CDMX..." /></div>
                                <div><label className="fm-label">Teléfono</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="fm-input" placeholder="+52..." /></div>
                                <div className="col-span-2"><label className="fm-label">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fm-input" /></div>
                            </div>

                            {/* ===== Bloque de Esquema de Compensación ===== */}
                            <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3 space-y-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900">Esquema de contratación y pago</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="fm-label">Tipo de contratación</label>
                                        <select
                                            value={form.employment_type || "planta"}
                                            onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                                            className="fm-input"
                                            data-testid="p-employment-type"
                                        >
                                            {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            {EMPLOYMENT_TYPES.find((t) => t.value === (form.employment_type || "planta"))?.hint}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="fm-label">Periodicidad de pago</label>
                                        <select
                                            value={form.pay_period || "mensual"}
                                            onChange={(e) => setForm({ ...form, pay_period: e.target.value })}
                                            className="fm-input"
                                            data-testid="p-pay-period"
                                        >
                                            {PAY_PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </div>

                                    {/* Sueldo mensual: solo si planta */}
                                    {form.employment_type === "planta" && (
                                        <div className="col-span-2">
                                            <label className="fm-label">Sueldo mensual (MXN)</label>
                                            <input type="number" min="0" step="100" value={form.salary} onChange={(e) => setForm({ ...form, salary: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-salary" />
                                        </div>
                                    )}

                                    {/* Tarifa por hora */}
                                    {(form.employment_type === "por_hora" || form.employment_type === "eventual" || form.employment_type === "freelance") && (
                                        <div>
                                            <label className="fm-label">Tarifa por hora (MXN/h)</label>
                                            <input type="number" min="0" step="10" value={form.hourly_rate || 0} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-hourly-rate" placeholder="Ej. 250" />
                                        </div>
                                    )}

                                    {/* Tarifas por guardia */}
                                    {form.employment_type === "por_guardia" && (
                                        <>
                                            <div>
                                                <label className="fm-label">Tarifa guardia 12h</label>
                                                <input type="number" min="0" step="50" value={form.shift_rate_12h || 0} onChange={(e) => setForm({ ...form, shift_rate_12h: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-shift-12h" placeholder="Ej. 1500" />
                                            </div>
                                            <div>
                                                <label className="fm-label">Tarifa guardia 24h</label>
                                                <input type="number" min="0" step="50" value={form.shift_rate_24h || 0} onChange={(e) => setForm({ ...form, shift_rate_24h: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-shift-24h" placeholder="Ej. 2800" />
                                            </div>
                                        </>
                                    )}

                                    {/* Pago por evento/servicio */}
                                    {(form.employment_type === "eventual" || form.employment_type === "freelance") && (
                                        <>
                                            <div>
                                                <label className="fm-label">Pago por evento / servicio</label>
                                                <input type="number" min="0" step="50" value={form.per_event_rate || 0} onChange={(e) => setForm({ ...form, per_event_rate: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-per-event" placeholder="Ej. 800" />
                                            </div>
                                            <div>
                                                <label className="fm-label">Bono extra por servicio</label>
                                                <input type="number" min="0" step="50" value={form.bonus_per_service || 0} onChange={(e) => setForm({ ...form, bonus_per_service: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" data-testid="p-bonus" placeholder="Opcional" />
                                            </div>
                                        </>
                                    )}

                                    {/* Por hora también puede tener bono opcional */}
                                    {form.employment_type === "por_hora" && (
                                        <div>
                                            <label className="fm-label">Bono extra por servicio (opcional)</label>
                                            <input type="number" min="0" step="50" value={form.bonus_per_service || 0} onChange={(e) => setForm({ ...form, bonus_per_service: parseFloat(e.target.value) || 0 })} className="fm-input font-mono" placeholder="Opcional" />
                                        </div>
                                    )}

                                    <div className="col-span-2">
                                        <label className="fm-label">Notas de nómina (opcional)</label>
                                        <input value={form.payroll_notes || ""} onChange={(e) => setForm({ ...form, payroll_notes: e.target.value })} className="fm-input" placeholder="Ej. paga vía transferencia, requiere CFDI, viáticos aparte..." />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">RFC</label><input value={form.rfc || ""} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} className="fm-input font-mono" /></div>
                                <div><label className="fm-label">CURP</label><input value={form.curp || ""} onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })} className="fm-input font-mono" /></div>
                                <div><label className="fm-label">Licencia/Cédula #</label><input value={form.license_number || ""} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Vence licencia</label><input type="date" value={form.license_expires || ""} onChange={(e) => setForm({ ...form, license_expires: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Contacto emergencia</label><input value={form.emergency_contact || ""} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Tel. emergencia</label><input value={form.emergency_phone || ""} onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Fecha de ingreso</label><input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="fm-input" /></div>
                                <div className="col-span-2"><label className="fm-label">Notas</label><textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="fm-input" /></div>
                            </div>
                            <div className="flex justify-between gap-2 pt-3 border-t border-slate-200">
                                <div>
                                    {editingId && (
                                        <button type="button" onClick={() => { setOpen(false); remove(list.find(x => x.id === editingId)); }} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1">
                                            <Trash2 className="w-3.5 h-3.5" /> Eliminar registro
                                        </button>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="fm-btn-outline">Cancelar</button>
                                    <button type="submit" className="fm-btn-accent" data-testid="save-personnel-btn">{editingId ? "Actualizar" : "Guardar"}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
